/**
 * Operational exception queue data hooks (OS Buddy — issue #1097, epic #1085).
 *
 * Server-backed via the permission-checked `/api/v1/operational-exceptions` API
 * (#1095 domain + #1096 capacity_overbooked). The list needs `scheduling.exceptions`;
 * the queue is the shared foreman view of the operational-exception domain.
 *
 * Enum unions mirror `@vehiclespace/shared` OPERATIONAL_EXCEPTION_* but are declared
 * inline (as the insights hook does) so the portal bundle never pulls server code.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export type ExceptionStatus =
  | 'open'
  | 'claimed'
  | 'released'
  | 'reassigned'
  | 'escalated'
  | 'stale'
  | 'resolved'
  | 'expired'
  | 'cancelled'
  | 'reopened';
export type ExceptionUrgency = 'low' | 'normal' | 'high' | 'critical';
export type ExceptionPolicy = 'single_owner' | 'assigned_owner' | 'two_person' | 'role_escalation';

/** A structured resolution option carried on a capacity_overbooked case (#1096). */
export interface CapacityResolutionOption {
  kind: 'overtime' | 'reschedule_customer' | 'reassign_manual';
  label: string;
  impact: {
    freedCapacityHours: number;
    overtimeHours: number;
    customerTimeChange: string;
    remainingRisk: 'low' | 'medium' | 'high';
  };
}

/** A resolution choice on an email_delivery_failed case (#3786); it carries no capacity impact. */
export interface EmailDeliveryResolutionOption {
  kind: 'fix_recipient_and_resend' | 'use_other_channel';
  label: string;
  impact?: undefined;
}

export type ExceptionResolutionOption = CapacityResolutionOption | EmailDeliveryResolutionOption;

export interface ExceptionTargetEntity {
  type: string;
  id: string;
}

export interface OperationalException {
  id: string;
  type: string;
  status: ExceptionStatus;
  urgency: ExceptionUrgency;
  decisionPolicy: ExceptionPolicy;
  requiredPermission: string | null;
  sourceRunId: string | null;
  evidence: Record<string, unknown> | null;
  targetEntities: ExceptionTargetEntity[] | null;
  resolutionOptions: ExceptionResolutionOption[] | null;
  assignedTo: string | null;
  assignedAt: string | null;
  slaDueAt: string | null;
  version: number;
  chosenResolution: unknown | null;
  resolutionRationale: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolutionResult: unknown | null;
  createdAt: string;
  updatedAt: string;
}

interface ExceptionListResponse {
  exceptions: OperationalException[];
  limit: number;
  offset: number;
}

export interface ExceptionFilters {
  [key: string]: string | number | undefined;
  status?: ExceptionStatus;
  limit?: number;
  offset?: number;
}

/** Statuses that still need a foreman's attention (the active queue). */
const ACTIVE_STATUSES: ReadonlySet<ExceptionStatus> = new Set([
  'open',
  'claimed',
  'released',
  'reassigned',
  'escalated',
  'stale',
  'reopened',
]);

export function isActiveException(exception: OperationalException): boolean {
  return ACTIVE_STATUSES.has(exception.status);
}

export function useExceptions(filters: ExceptionFilters = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.exceptions.list(filters),
    queryFn: () => api.get<ExceptionListResponse>('/operational-exceptions', { params: filters }),
    staleTime: 15 * 1000,
    enabled: options?.enabled,
  });
}

/** One append-only audit row on a case (`operational_exception_events`). */
export interface OperationalExceptionEvent {
  id: string;
  exceptionId: string;
  seq: number;
  eventType: string;
  actorType: string | null;
  actorId: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface ExceptionDetailResponse {
  exception: OperationalException;
  events: OperationalExceptionEvent[];
}

export function useExceptionDetail(id: string | null) {
  return useQuery({
    queryKey: queryKeys.exceptions.detail(id ?? ''),
    queryFn: () => api.get<ExceptionDetailResponse>(`/operational-exceptions/${id ?? ''}`),
    enabled: !!id,
  });
}

interface ExceptionMutationResponse {
  exception: OperationalException;
  awaitingSecondApproval?: boolean;
}

/**
 * Invalidate both the queue lists and the case detail after any transition, so the
 * acting foreman sees the new state immediately (other foremen get it over the
 * `operational_exceptions` collection socket).
 */
function useInvalidateExceptions() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.exceptions.all });
  };
}

/** Atomically take ownership of an open case (design §9 two-tier: scheduling.exceptions). */
export function useClaimException() {
  const invalidate = useInvalidateExceptions();
  return useMutation({
    mutationFn: (vars: { id: string }) =>
      api.post<ExceptionMutationResponse>(`/operational-exceptions/${vars.id}/claim`, {}),
    onSuccess: invalidate,
  });
}

export function useReleaseException() {
  const invalidate = useInvalidateExceptions();
  return useMutation({
    mutationFn: (vars: { id: string; expectedVersion: number; reason?: string }) =>
      api.post<ExceptionMutationResponse>(`/operational-exceptions/${vars.id}/release`, {
        expectedVersion: vars.expectedVersion,
        reason: vars.reason,
      }),
    onSuccess: invalidate,
  });
}

export function useReassignException() {
  const invalidate = useInvalidateExceptions();
  return useMutation({
    mutationFn: (vars: { id: string; expectedVersion: number; newAssigneeId: string; reason?: string }) =>
      api.post<ExceptionMutationResponse>(`/operational-exceptions/${vars.id}/reassign`, {
        expectedVersion: vars.expectedVersion,
        newAssigneeId: vars.newAssigneeId,
        reason: vars.reason,
      }),
    onSuccess: invalidate,
  });
}

/**
 * Record a resolution (needs scheduling.exceptions.resolve). `chosenResolution` is
 * the decision the foreman took (e.g. which capacity option); `rationale` is the note.
 * A `two_person` policy returns `awaitingSecondApproval` on the first approval.
 */
export function useResolveException() {
  const invalidate = useInvalidateExceptions();
  return useMutation({
    mutationFn: (vars: { id: string; expectedVersion: number; chosenResolution: unknown; rationale?: string }) =>
      api.post<ExceptionMutationResponse>(`/operational-exceptions/${vars.id}/resolve`, {
        expectedVersion: vars.expectedVersion,
        chosenResolution: vars.chosenResolution,
        rationale: vars.rationale,
      }),
    onSuccess: invalidate,
  });
}

/** Reopen a resolved case (needs scheduling.exceptions.override). */
export function useReopenException() {
  const invalidate = useInvalidateExceptions();
  return useMutation({
    mutationFn: (vars: { id: string; expectedVersion: number; reason?: string }) =>
      api.post<ExceptionMutationResponse>(`/operational-exceptions/${vars.id}/reopen`, {
        expectedVersion: vars.expectedVersion,
        reason: vars.reason,
      }),
    onSuccess: invalidate,
  });
}
